import React from 'react';
import { Button, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';

import useGridExport from './useGridExport';

/**
 * Export control for a server-side data grid.
 *
 * Reusable across grids: it owns the whole flow (build the query string, POST,
 * download or track a background task) through `useGridExport`. A caller only
 * hands it the same filter props it hands RecordListPro.
 *
 * With one format it renders a plain button; with several, a dropdown. The app
 * has no ButtonGroup split button anywhere, so this follows the existing
 * Button + Menu idiom instead of introducing a new control.
 */
const FORMAT_LABELS = {
    csv: { label: 'Export as CSV', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    xlsx: { label: 'Export as Excel (XLSX)', icon: <GridOnOutlinedIcon fontSize="small" /> },
    xml: { label: 'Export as XML (import format)…', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    json: { label: 'Export as JSON (import format)…', icon: <DescriptionOutlinedIcon fontSize="small" /> },
};

const ExportButton = ({
    resource,
    apiFilters,
    query,
    extraParams,
    urlSuffix,
    selectedIds,
    isAllSelected,
    matchingCount,
    filenameFallback,
    formats = ['csv', 'xlsx'],
    // Formats that open the XML/JSON options dialog instead of exporting
    // directly — the caller renders EntityExportDialog and handles this.
    documentFormats = [],
    onDocumentFormat = null,
    size = 'small',
    disabled = false,
    // Icon only, for a page header on a phone where the add button next to it
    // is icon-only too (CP-8291). The label — including any selection count —
    // moves into a tooltip; the grid footer still states the count.
    hideLabel = false,
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const { exportGrid, exporting } = useGridExport({
        resource, apiFilters, query, extraParams, urlSuffix, selectedIds, isAllSelected,
        filenameFallback,
    });

    const selectedCount = (selectedIds || []).length;
    // Say which rows will be in the file. Without this the user cannot tell
    // whether ticking boxes narrowed the export or not.
    //
    // Under "select all matching" the grid only holds a capped id list, so
    // selectedIds.length is NOT what gets exported — the server resolves the
    // filters instead. Label it with the true match count so the button never
    // promises 5,000 rows and deliver 40,000 (or vice versa).
    let label = 'Export';
    if (isAllSelected) {
        label = matchingCount
            ? `Export (all ${matchingCount.toLocaleString()} matching)`
            : 'Export (all matching)';
    } else if (selectedCount) {
        label = `Export (${selectedCount.toLocaleString()} selected)`;
    }
    const close = () => setAnchorEl(null);

    const run = (format) => {
        close();
        exportGrid(format);
    };

    const runDocument = (format) => {
        close();
        if (onDocumentFormat) onDocumentFormat(format);
    };

    const allFormats = [...formats, ...documentFormats];
    const icon = exporting ? <CircularProgress size={14} /> : <FileUploadOutlinedIcon />;

    // MUI's startIcon/endIcon assume text between them; with no text they
    // leave lopsided margins, so the icon-only variant renders the icon as a
    // plain child and carries the label in a tooltip instead. It drops the
    // menu arrow too, so it is the same square button as the Activities
    // phone Export, which opens a dialog rather than a menu (CP-8292).
    const renderButton = (onClick, withMenuArrow) => (hideLabel ? (
        <Tooltip title={label}>
            <span>
                <Button
                    onClick={onClick}
                    variant="outlined"
                    color="primary"
                    size={size}
                    disabled={disabled || exporting}
                    aria-label={label}
                    sx={{ minWidth: 0 }}
                >
                    {icon}
                </Button>
            </span>
        </Tooltip>
    ) : (
        <Button
            onClick={onClick}
            variant="outlined"
            color="primary"
            size={size}
            disabled={disabled || exporting}
            sx={{ whiteSpace: 'nowrap' }}
            startIcon={icon}
            endIcon={withMenuArrow ? <ArrowDropDownIcon /> : undefined}
        >
            {label}
        </Button>
    ));

    if (allFormats.length === 1 && !documentFormats.length) {
        return renderButton(() => run(formats[0]), false);
    }

    return (
        <>
            {renderButton((ev) => setAnchorEl(ev.currentTarget), true)}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
                {allFormats.map((format) => {
                    const meta = FORMAT_LABELS[format]
                        || { label: `Export as ${format.toUpperCase()}`, icon: null };
                    const isDocument = documentFormats.includes(format);
                    return (
                        <MenuItem
                            key={format}
                            onClick={() => (isDocument ? runDocument(format) : run(format))}
                        >
                            {meta.icon && <ListItemIcon>{meta.icon}</ListItemIcon>}
                            <ListItemText>{meta.label}</ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default ExportButton;
